/**
 * The driver drill-down ("Active Context") shown beside the task feed. Lets a
 * dispatcher validate a recommendation at a glance: context tiles, the relevant
 * HOS clock (hover for all four), parallel Appointment-risk and PTA-risk boxes,
 * and a traffic-colored route map.
 */

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import FlagIcon from "@mui/icons-material/Flag";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import PersonPinCircleIcon from "@mui/icons-material/PersonPinCircle";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import RvHookupIcon from "@mui/icons-material/RvHookup";
import SpeedIcon from "@mui/icons-material/Speed";
import StorefrontIcon from "@mui/icons-material/Storefront";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";

import { cityStateCode, companyCode } from "../codes";
import { accent, text, zinc } from "../colors";
import { hash } from "../hash";
import type { Driver, DriverStatusCode, EmptyCallFailure, RiskInfo } from "../types";
import { ActionButton, Chip, CodeBadge, FieldLabel, Panel } from "./primitives";
import { RouteMap } from "./RouteMap";
import { StopsTimeline } from "./StopsTimeline";

/** Edits the dispatcher can push back to AS400 from the context panel. */
export interface DriverEdits {
  status: DriverStatusCode;
  statusLabel: string;
  /** Appointment time is customer-set and read-only for these users — not edited here. */
  apptProjected?: string;
  ptaTarget?: string;
  ptaProjected?: string;
}

const STATUS_OPTIONS: { code: DriverStatusCode; label: string }[] = [
  { code: "AC", label: "Available" },
  { code: "C", label: "Covered" },
  { code: "T", label: "In transit" },
  { code: "BD", label: "Broken down" },
];

const fieldInputSx = {
  fontFamily: "inherit",
  bgcolor: zinc[900],
  border: `1px solid ${zinc[700]}`,
  borderRadius: "6px",
  px: 0.75,
  py: 0.5,
  "&:focus": { outline: "none", borderColor: accent.purpleBorder },
} as const;

// Shared vertical metrics so the read-only target value and the editable projected
// value have identical heights — keeps the "X vs Projected" rows aligned across boxes.
const riskValueSx = {
  mt: 0.25,
  py: 0.5,
  fontSize: 24,
  fontWeight: 800,
  lineHeight: 1.1,
} as const;

interface DriverDraft {
  status: DriverStatusCode;
  apptProjected: string;
  ptaTarget: string;
  ptaProjected: string;
}

/** The risk `target` is a "<label…> <time>" string; we only edit the trailing time token. */
function targetLabel(target?: string): string {
  return target?.split(" ").slice(0, -1).join(" ") || "Target";
}

function targetTime(target?: string): string {
  return target?.split(" ").slice(-1)[0] ?? "";
}

function makeDraft(driver: Driver): DriverDraft {
  return {
    status: driver.status,
    apptProjected: driver.appointmentRisk?.projected ?? "",
    ptaTarget: targetTime(driver.pta?.risk.target),
    ptaProjected: driver.pta?.risk.projected ?? "",
  };
}

/** Deterministic demo trailer number for a driver. */
function trailerNumber(driverId: string): string {
  return `5${String(3000 + (hash(`trailer::${driverId}`) % 6000)).padStart(4, "0")}`;
}

const FUEL_STOP_BRANDS = ["Pilot", "Love's", "TA", "Flying J", "Petro"];

/** Plausible interstate exits around the Nashville freight lanes. */
const FUEL_STOP_EXITS = [
  "Interstate 40 exit 210",
  "Interstate 40 exit 172",
  "Interstate 24 exit 62",
  "Interstate 24 exit 11",
  "Interstate 65 exit 98",
  "Interstate 65 exit 117",
  "Interstate 840 exit 42",
  "Interstate 440 exit 3",
];

/**
 * Next scheduled fueling location on the driver's route (demo). The fuel
 * solution is sent with every load; this surfaces it so a dispatcher can answer
 * "which one am I going to?" without looking it up in AS400.
 */
function nextFuelStop(driverId: string): { name: string; description: string } {
  const brand = FUEL_STOP_BRANDS[hash(`fuelbrand::${driverId}`) % FUEL_STOP_BRANDS.length];
  const storeNumber = 12 + (hash(`fuelstore::${driverId}`) % 700);
  return {
    name: `${brand} #${storeNumber}`,
    description: FUEL_STOP_EXITS[hash(`fuelexit::${driverId}`) % FUEL_STOP_EXITS.length],
  };
}

function appointmentLabel(driver: Driver): string {
  const appt = driver.appointment;
  if (!appt) return "—";
  if (appt.kind === "window") return `${appt.start}–${appt.end}`;
  return appt.time ?? "—";
}

function ContextTile({
  icon,
  label,
  value,
  accentColor,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  accentColor?: string;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, py: 1.25, px: 1.5 }}>
      <Box sx={{ color: accentColor ?? text.muted, mt: "1px" }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <FieldLabel>{label}</FieldLabel>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: text.primary, mt: 0.25 }}>{value}</Typography>
      </Box>
    </Box>
  );
}

function StatusTile({ value, onChange }: { value: DriverStatusCode; onChange: (code: DriverStatusCode) => void }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, py: 1.25, px: 1.5 }}>
      <Box sx={{ color: value === "BD" ? accent.red : accent.green, mt: "1px" }}>
        <PersonPinCircleIcon sx={{ fontSize: 18 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <FieldLabel>Status</FieldLabel>
        <Box
          component="select"
          value={value}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.target.value as DriverStatusCode)}
          sx={{
            ...fieldInputSx,
            mt: 0.25,
            fontSize: 14,
            fontWeight: 600,
            color: text.primary,
            cursor: "pointer",
          }}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.code} value={o.code}>
              {o.code} ({o.label})
            </option>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function HosTile({ driver }: { driver: Driver }) {
  const hos = driver.hos;
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, py: 1.25, px: 1.5 }}>
      <Box sx={{ color: text.muted, mt: "1px" }}>
        <AccessTimeIcon sx={{ fontSize: 18 }} />
      </Box>
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <FieldLabel>HOS ({hos.primaryLabel.toLowerCase()})</FieldLabel>
          <Tooltip
            arrow
            placement="top"
            title={
              <Box sx={{ p: 0.5 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: text.secondary, mb: 0.5 }}>
                  All clocks remaining
                </Typography>
                {hos.clocks.map((c) => (
                  <Box key={c.label} sx={{ display: "flex", justifyContent: "space-between", gap: 3, py: 0.25 }}>
                    <Typography sx={{ fontSize: 12.5, color: text.secondary }}>{c.label}</Typography>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: text.primary }}>{c.value}</Typography>
                  </Box>
                ))}
              </Box>
            }
          >
            <InfoOutlinedIcon sx={{ fontSize: 14, color: text.muted, cursor: "help" }} />
          </Tooltip>
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: text.primary, mt: 0.25 }}>
          {hos.primaryValue}
          {hos.primaryContext && (
            <Box component="span" sx={{ color: text.muted, fontWeight: 500 }}>
              {" "}
              · {hos.primaryContext}
            </Box>
          )}
        </Typography>
      </Box>
    </Box>
  );
}

function RiskBox({
  title,
  risk,
  targetDraft,
  onTargetChange,
  projectedDraft,
  onProjectedChange,
}: {
  title: string;
  risk?: RiskInfo;
  targetDraft?: string;
  onTargetChange?: (value: string) => void;
  projectedDraft?: string;
  onProjectedChange?: (value: string) => void;
}) {
  const atRisk = risk?.atRisk ?? false;
  return (
    <Panel sx={{ flex: 1, p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
        <FlagIcon sx={{ fontSize: 17, color: atRisk ? accent.red : accent.green }} />
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: text.primary }}>{title}</Typography>
      </Box>

      {!risk ? (
        <Typography sx={{ fontSize: 13, color: text.muted }}>No appointment on this load.</Typography>
      ) : (
        <>
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
            {/* left: the plan / target time we have in our system (editable) */}
            <Box>
              <FieldLabel>{targetLabel(risk.target)}</FieldLabel>
              {onTargetChange ? (
                <Box
                  component="input"
                  value={targetDraft ?? ""}
                  placeholder="HH:MM"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onTargetChange(e.target.value)}
                  sx={{ ...riskValueSx, ...fieldInputSx, width: 104, color: text.primary }}
                />
              ) : (
                // transparent border + matching padding keep this aligned with the editable input opposite
                <Typography sx={{ ...riskValueSx, color: text.primary, border: "1px solid transparent" }}>
                  {risk.target?.split(" ").slice(-1)[0] ?? "—"}
                </Typography>
              )}
            </Box>
            <Typography sx={{ fontSize: 13, color: text.muted, pb: 1 }}>vs</Typography>
            {/* right: the projected time (editable) */}
            <Box>
              <FieldLabel>Projected</FieldLabel>
              {onProjectedChange ? (
                <Box
                  component="input"
                  value={projectedDraft ?? ""}
                  placeholder="HH:MM"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onProjectedChange(e.target.value)}
                  sx={{
                    ...riskValueSx,
                    ...fieldInputSx,
                    width: 104,
                    color: atRisk ? accent.red : accent.green,
                  }}
                />
              ) : (
                <Typography
                  sx={{ ...riskValueSx, color: atRisk ? accent.red : accent.green, border: "1px solid transparent" }}
                >
                  {risk.projected ?? "—"}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
            {risk.delta && (
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: atRisk ? accent.red : accent.green }}>
                {atRisk ? risk.delta : "On time"}
              </Typography>
            )}
            <Chip label={atRisk ? "AT RISK" : "ON TRACK"} tone={atRisk ? "red" : "green"} />
          </Box>
          {atRisk && risk.reason && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1.25 }}>
              <Typography sx={{ fontSize: 12, color: text.muted }}>Reason:</Typography>
              <Chip label={risk.reason} tone="neutral" />
            </Box>
          )}
        </>
      )}
    </Panel>
  );
}

/**
 * Indication that the driver's preplan failed to dispatch. The empty call still
 * happens on delivery; this flags the next-load dispatch that needs dispatcher
 * action — correct the trailer number, or approve the deadhead miles. Resolution
 * is demo-only local state.
 */
function EmptyCallFailureBox({ failure }: { failure: EmptyCallFailure }) {
  const [editing, setEditing] = useState(false);
  const [trailer, setTrailer] = useState("");
  const [resolution, setResolution] = useState<string | null>(null);

  if (resolution) {
    return (
      <Panel sx={{ p: 2, mb: 2, borderColor: accent.greenBorder, bgcolor: accent.greenSoft }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <CheckCircleIcon sx={{ fontSize: 18, color: accent.green }} />
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: text.primary }}>{resolution}</Typography>
        </Box>
      </Panel>
    );
  }

  return (
    <Panel sx={{ p: 2, mb: 2, borderColor: accent.amberBorder, bgcolor: accent.amberSoft }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
        <WarningAmberIcon sx={{ fontSize: 18, color: accent.amber }} />
        <Typography sx={{ fontSize: 14, fontWeight: 800, color: accent.amber }}>Preplan failed to dispatch</Typography>
      </Box>
      <Typography sx={{ fontSize: 13, color: text.secondary, mb: 1.5 }}>
        <Box component="span" sx={{ color: text.muted }}>
          Reason:{" "}
        </Box>
        {failure.reason}
      </Typography>
      {failure.kind === "mileage" ? (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <ActionButton
            label="Approve deadhead & dispatch"
            onClick={() => setResolution("Deadhead miles approved — preplan dispatched")}
          />
        </Box>
      ) : editing ? (
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, flexWrap: "wrap" }}>
          <Box sx={{ minWidth: 0 }}>
            <FieldLabel>Trailer number</FieldLabel>
            <Box
              component="input"
              value={trailer}
              placeholder="e.g. 53814"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTrailer(e.target.value)}
              sx={{ ...fieldInputSx, mt: 0.25, fontSize: 14, color: text.primary }}
            />
          </Box>
          <ActionButton
            label="Dispatch preplan"
            onClick={() => trailer.trim() && setResolution(`Preplan dispatched with trailer ${trailer.trim()}`)}
          />
          <ActionButton label="Cancel" variant="ghost" onClick={() => setEditing(false)} />
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <ActionButton label="Override trailer number" onClick={() => setEditing(true)} />
          <ActionButton
            label="Override manually"
            variant="ghost"
            onClick={() => setResolution("Preplan dispatched (manual override)")}
          />
        </Box>
      )}
    </Panel>
  );
}

export function ActiveContextPanel({
  driver,
  onSave,
  onOpenMessageHistory,
  onAssign,
  onClose,
}: {
  driver: Driver;
  onSave?: (driverId: string, edits: DriverEdits) => void;
  onOpenMessageHistory?: () => void;
  /** When provided, renders an "Assign" button that hands the driver's item off. */
  onAssign?: () => void;
  /** When provided, renders a close button in the header (used when shown as a side panel). */
  onClose?: () => void;
}) {
  const [draft, setDraft] = useState<DriverDraft>(() => makeDraft(driver));
  useEffect(() => setDraft(makeDraft(driver)), [driver.id]);

  const hasAppt = Boolean(driver.appointmentRisk);
  const hasPta = Boolean(driver.pta);
  const dirty =
    draft.status !== driver.status ||
    (hasAppt && draft.apptProjected !== (driver.appointmentRisk?.projected ?? "")) ||
    (hasPta &&
      (draft.ptaProjected !== (driver.pta?.risk.projected ?? "") ||
        draft.ptaTarget !== targetTime(driver.pta?.risk.target)));

  const handleSave = () => {
    const option = STATUS_OPTIONS.find((o) => o.code === draft.status) ?? STATUS_OPTIONS[0];
    onSave?.(driver.id, {
      status: draft.status,
      statusLabel: option.label,
      apptProjected: hasAppt ? draft.apptProjected : undefined,
      ptaTarget: hasPta ? draft.ptaTarget : undefined,
      ptaProjected: hasPta ? draft.ptaProjected : undefined,
    });
  };

  const fuelStop = nextFuelStop(driver.id);

  const velocityValue = (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      {driver.velocityMph} mph
      {driver.velocityTrend === "down" && <TrendingDownIcon sx={{ fontSize: 15, color: accent.red }} />}
      {driver.velocityTrend === "down" && (
        <Box component="span" sx={{ color: text.muted, fontWeight: 500 }}>
          (traffic)
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ p: 2.5, overflowY: "auto", height: "100%" }}>
      {/* header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: text.primary }}>
          Active Context — {driver.name}
        </Typography>
        <Typography sx={{ fontSize: 13, color: text.muted }}>Truck {driver.truck}</Typography>
        <Chip label={`${driver.divisionLabel} · Div ${driver.division}`} tone="blue" />
        {(onOpenMessageHistory || onAssign || onClose) && <Box sx={{ flex: 1 }} />}
        {onAssign && (
          <Box
            component="button"
            type="button"
            onClick={onAssign}
            sx={{
              cursor: "pointer",
              fontFamily: "inherit",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.625,
              fontSize: 13,
              fontWeight: 600,
              color: text.secondary,
              bgcolor: "transparent",
              border: `1px solid ${zinc[700]}`,
              borderRadius: "8px",
              px: 1.25,
              py: 0.75,
              whiteSpace: "nowrap",
              transition: "all 120ms ease",
              "&:hover": { color: text.primary, borderColor: zinc[600] },
            }}
          >
            <PersonAddAltOutlinedIcon sx={{ fontSize: 16 }} />
            Assign (A)
          </Box>
        )}
        {onOpenMessageHistory && (
          <Box
            component="button"
            type="button"
            onClick={onOpenMessageHistory}
            sx={{
              cursor: "pointer",
              fontFamily: "inherit",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.625,
              fontSize: 13,
              fontWeight: 600,
              color: accent.blue,
              bgcolor: "transparent",
              border: `1px solid ${accent.blueBorder}`,
              borderRadius: "8px",
              px: 1.25,
              py: 0.75,
              whiteSpace: "nowrap",
              transition: "all 120ms ease",
              "&:hover": { bgcolor: accent.blueSoft },
            }}
          >
            <ForumOutlinedIcon sx={{ fontSize: 16 }} />
            Open message history
          </Box>
        )}
        {onClose && (
          <Box
            component="button"
            type="button"
            aria-label="Close active context"
            onClick={onClose}
            sx={{
              cursor: "pointer",
              fontFamily: "inherit",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: text.secondary,
              bgcolor: "transparent",
              border: `1px solid ${zinc[700]}`,
              borderRadius: "8px",
              p: 0.625,
              transition: "all 120ms ease",
              "&:hover": { color: text.primary, borderColor: zinc[600] },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </Box>
        )}
      </Box>

      {driver.emptyCallFailure && <EmptyCallFailureBox failure={driver.emptyCallFailure} />}

      {/* save bar — appears once any AS400-backed field is edited */}
      {dirty && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <Box
            component="button"
            type="button"
            onClick={handleSave}
            sx={{
              cursor: "pointer",
              fontFamily: "inherit",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              fontSize: 13,
              fontWeight: 700,
              px: 1.75,
              py: 1,
              borderRadius: "8px",
              color: "#fff",
              bgcolor: accent.purple,
              border: `1px solid ${accent.purple}`,
              transition: "all 120ms ease",
              "&:hover": { bgcolor: "#8a0fde", borderColor: "#8a0fde" },
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 17 }} />
            Save and push to AS400
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => setDraft(makeDraft(driver))}
            sx={{
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: 600,
              color: text.secondary,
              bgcolor: "transparent",
              border: "none",
              "&:hover": { color: text.primary },
            }}
          >
            Reset
          </Box>
        </Box>
      )}

      {/* context tiles */}
      <Panel sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            columnGap: 1,
            rowGap: 0,
            "& > *": { borderBottom: `1px solid ${zinc[850]}` },
          }}
        >
          <HosTile driver={driver} />
          <ContextTile
            icon={<FlagIcon sx={{ fontSize: 18 }} />}
            label="Destination"
            value={
              <>
                {driver.destination ?? "—"}
                <CodeBadge code={cityStateCode(driver.destination)} />
              </>
            }
          />
          <StatusTile value={draft.status} onChange={(code) => setDraft((d) => ({ ...d, status: code }))} />
          <ContextTile
            icon={<Inventory2Icon sx={{ fontSize: 18 }} />}
            label="Load type"
            value={driver.loadType ?? "—"}
          />
          <ContextTile icon={<RvHookupIcon sx={{ fontSize: 18 }} />} label="Trailer" value={trailerNumber(driver.id)} />
          <ContextTile
            icon={<LocationOnIcon sx={{ fontSize: 18 }} />}
            label="Location"
            value={
              <>
                {driver.location}
                <CodeBadge code={cityStateCode(driver.location)} />
              </>
            }
          />
          <ContextTile
            icon={<EventAvailableIcon sx={{ fontSize: 18 }} />}
            label="Next stop"
            value={driver.nextStop ?? "—"}
          />
          <ContextTile icon={<SpeedIcon sx={{ fontSize: 18 }} />} label="Velocity" value={velocityValue} />
          <ContextTile
            icon={<CalendarMonthIcon sx={{ fontSize: 18 }} />}
            label="Appointment"
            value={
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                {appointmentLabel(driver)}
                {driver.appointment && (
                  <Chip label={driver.appointment.kind === "window" ? "WINDOW" : "STRICT"} tone="neutral" />
                )}
              </Box>
            }
          />
          <ContextTile
            icon={<LocalGasStationIcon sx={{ fontSize: 18 }} />}
            label="Fuel"
            value={driver.fuelLevel != null ? `${driver.fuelLevel}%` : "—"}
            accentColor={driver.fuelLevel != null && driver.fuelLevel < 25 ? accent.red : undefined}
          />
          <ContextTile
            icon={<StorefrontIcon sx={{ fontSize: 18 }} />}
            label="Customer"
            value={
              driver.customer ? (
                <>
                  {driver.customer}
                  <CodeBadge code={companyCode(driver.customer)} />
                  {driver.customerCity && (
                    <>
                      {" — "}
                      {driver.customerCity}
                      <CodeBadge code={cityStateCode(driver.customerCity)} />
                    </>
                  )}
                </>
              ) : (
                "—"
              )
            }
          />
          <ContextTile
            icon={<LocalGasStationIcon sx={{ fontSize: 18 }} />}
            label="Next fuel stop"
            value={
              <>
                {fuelStop.name}
                <Box component="span" sx={{ color: text.muted, fontWeight: 500 }}>
                  {" "}
                  · {fuelStop.description}
                </Box>
              </>
            }
          />
        </Box>
      </Panel>

      {/* parallel risk boxes */}
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <RiskBox
          title="Appointment risk"
          risk={driver.appointmentRisk}
          projectedDraft={draft.apptProjected}
          onProjectedChange={hasAppt ? (v) => setDraft((d) => ({ ...d, apptProjected: v })) : undefined}
        />
        <RiskBox
          title="PTA risk"
          risk={driver.pta?.risk}
          targetDraft={draft.ptaTarget}
          onTargetChange={hasPta ? (v) => setDraft((d) => ({ ...d, ptaTarget: v })) : undefined}
          projectedDraft={draft.ptaProjected}
          onProjectedChange={hasPta ? (v) => setDraft((d) => ({ ...d, ptaProjected: v })) : undefined}
        />
      </Box>

      {/* stops timeline */}
      {driver.stops && driver.stops.length > 0 && (
        <Panel sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.75 }}>
            <RouteOutlinedIcon sx={{ fontSize: 17, color: text.secondary }} />
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: text.primary }}>Stops</Typography>
            <Typography sx={{ fontSize: 12.5, color: text.muted }}>
              {driver.stops.filter((s) => s.state === "completed").length} done ·{" "}
              {driver.stops.filter((s) => s.state === "upcoming").length} upcoming
            </Typography>
          </Box>
          <StopsTimeline stops={driver.stops} />
        </Panel>
      )}

      {/* route map */}
      <Panel sx={{ p: 2 }}>
        <RouteMap route={driver.route} />
      </Panel>
    </Box>
  );
}
